import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as yup from 'yup';

import {
  Box,
  Container,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Button,
  Tooltip
} from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox as CheckBoxIcon, Help } from '@mui/icons-material';
import {
  geocodeByAddress,
} from 'react-places-autocomplete';

import { ProfileInfoHeader } from '../shared/ProfileInfoHeader';
import { FormPageNavBtns } from '../shared/FormPageNavBtns';
import { FormCardWrap } from '../shared/FormCardWrap';
import { GooglePlaceSearch } from '../shared/GooglePlaceSearch';
import { dropDownConstList } from '../../config/dropDownConstList';
// hooks
import useWindowSize from '../../hooks/useWindowSize';

import { profileInfoAction } from '../../redux/profile/Reducer';
import { formCircleBtnNavLink } from '../../utils/generalFunc';

import { useStyles } from './style';

const label = { inputProps: { 'aria-label': 'Checkbox' } };

// no errors then disable or (if there is no values in required field and form is not touched then disable(in case of fresh form))
const nextBtnDisabledFunc = (formik) => (Object.keys(formik.errors).length !== 0)
  || (Object.keys(formik.touched).length === 0
    && (formik.values.address === '' || !formik.values.address)
    && (formik.values.city === '' || !formik.values.city)
    && (formik.values.state === '' || !formik.values.state)
    && (formik.values.postalCode === '' || !formik.values.postalCode)
    && (formik.values.bioSex === '' || !formik.values.bioSex)
    && (formik.values.race === '' || !formik.values.race)
    && (formik.values.primaryLang === '' || !formik.values.primaryLang)
  );

export const PersonalInfoForm = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile.profilInfo);
  const allowDonorPhoto = useSelector((state) => state.profile.allowDonorPhotoUpload);

  const [bioSextooltip, setBioSextooltip] = useState(false);
  const windowSize = useWindowSize();

  const profilrEthnicity = profile.Ethnicity;
  const ethnicity = profilrEthnicity?.toLowerCase() === 'white' ? 'Caucasian' : profilrEthnicity;

  const formik = useFormik({
    initialValues: {
      address: profile.HomeAddressLine1 || '',
      aptNo: profile.HomeAddressLine2 || '',
      city: profile.City || '',
      state: profile.State || '',
      postalCode: profile.ZipCode || '',
      ssn: profile.SSN || '',
      bioSex: profile.Gender && dropDownConstList.biologicalSexList.includes(profile.Gender) ? profile.Gender : '',
      race: ethnicity && dropDownConstList.raceList.includes(ethnicity) ? ethnicity : '',
      primaryLang: profile.PrimaryLanguage && dropDownConstList.primaryLanguageList.includes(profile.PrimaryLanguage) ? profile.PrimaryLanguage : '',
      email: profile.EmailAddress || '',
      phNo: profile.Phone || '',
      smsNotifyAgree: profile.SMSEnrollment || false,
    },
    validationSchema: yup.object({
      address: yup
        .string()
        .trim()
        .required('This field is required*'),
      aptNo: yup
        .string()
        .trim(),
      city: yup
        .string()
        .trim()
        .required('This field is required*'),
      state: yup
        .string()
        .trim()
        .required('This field is required*'),
      postalCode: yup
        .string()
        .trim()
        .required('This field is required*'),
      ssn: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /^((?!000|666)[0-8][0-9]{2}-(?!00)[0-9]{2}-(?!0000)[0-9]{4})|((?!000|666)[0-8][0-9]{2}(?!00)[0-9]{2}(?!0000)[0-9]{4}$)$/,
          'Enter a valid Social Security #'
        ),
      bioSex: yup
        .string()
        .trim()
        .required('This field is required*'),
      race: yup
        .string()
        .trim()
        .required('This field is required*'),
      primaryLang: yup
        .string()
        .trim()
        .required('This field is required*'),
      email: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          'Enter a valid email'
        ),
      phNo: yup
        .string()
        .matches(
          /* eslint-disable no-useless-escape */
          /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/,
          'Enter a valid phone number'
        ),
      smsNotifyAgree: yup.bool()
    }),
    onSubmit: (data) => {
      // go into next page if statement if button is enabled
      if (!nextBtnDisabledFunc(formik)) {
        dispatch(profileInfoAction({
          ...profile,
          HomeAddressLine1: data.address,
          HomeAddressLine2: data.aptNo,
          City: data.city,
          State: data.state,
          ZipCode: data.postalCode,
          SSN: data.ssn,
          Gender: data.bioSex,
          Ethnicity: data.race,
          PrimaryLanguage: data.primaryLang,
          EmailAddress: data.email,
          Phone: data.phNo,
          SMSEnrollment: data.smsNotifyAgree,
        }));
        history.push('/insurance-info');
      }
    },
  });

  // eslint-disable-next-line no-shadow
  const onAddressChange = (add) => {
    formik.setFieldValue('address', add);
  };

  // eslint-disable-next-line no-shadow
  const handleSelect = (add) => {
    geocodeByAddress(add)
      .then((results) => {
        const [addressArr] = results;
        const { long_name: streetNumber = '' } = addressArr.address_components.find((c) => c.types.includes('street_number')) || {};
        const { long_name: route = '' } = addressArr.address_components.find((c) => c.types.includes('route')) || {};
        const { long_name: sublocality1 = '' } = addressArr.address_components.find((c) => c.types.includes('sublocality_level_1')) || {};
        const { long_name: postalCode = '' } = addressArr.address_components.find((c) => c.types.includes('postal_code')) || {};
        const { long_name: city = '' } = addressArr.address_components.find((c) => c.types.includes('locality')) || {};
        const { long_name: state = '' } = addressArr.address_components.find((c) => c.types.includes('administrative_area_level_1')) || {};
        const completeAddress = `${streetNumber && `${streetNumber}, `}${route && `${route}, `}`;
        const cityWithDistrict = `${city ? sublocality1 && `${sublocality1}, ` : `${sublocality1}`
          }${city && `${city}`}`;

        formik.setFieldValue('address', `${completeAddress.trim().slice(0, -1)}` || '');
        formik.setFieldValue('city', cityWithDistrict);
        formik.setFieldValue('state', state);
        formik.setFieldValue('postalCode', postalCode);
      })
      .catch((error) => console.error('Error', error));
  };

  const searchOptions = {
    // eslint-disable-next-line no-undef
    location: new google.maps.LatLng(41.850033, -87.6500523),
    radius: 20000,
    componentRestrictions: { country: ['us'] },
    types: ['address']
  };

  const ssnOnChangeHandler = (e) => {
    formik.setFieldValue('ssn', e.target.value);
  };

  const onDotClick = (dotNum) => {
    formCircleBtnNavLink(history, dotNum);
  };

  const BackClickHandler = () => {
    if (allowDonorPhoto) {
      history.push('/camera-take');
    } else {
      history.push('/welcome');
    }
  };

  return (
    <Container maxWidth="md" className={classes.personalInfoContainer}>
      <ProfileInfoHeader />
      <FormCardWrap cardsBgNumber={3}>
        <form onSubmit={formik.handleSubmit} className="formContainer">
          <Box className={classes.personalContentWrap}>
            <Box className={classes.formPageNumber}>1/4</Box>
            <Box className={classes.contentTitleWrap}>
              <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine1} `} />
              <Box className={classes.contentTitle}>Personal Information</Box>
              <Box component="span" className={`${classes.contentTitleLine} ${classes.titleLine2} `} />
            </Box>
            <Box className={`${classes.addrAptContainer} ${classes.subTextColor} `}>
              <Box className={classes.addrContainer}>
                <Box className={`${classes.addrLabel} commonLabel`} component="label">&nbsp;Address</Box>
                <GooglePlaceSearch
                  value={formik.values.address || ''}
                  onChange={onAddressChange}
                  onSelect={handleSelect}
                  searchOptions={searchOptions}
                  debounce={1000}
                  onBlur={() => formik.setFieldTouched('address')}
                />
                {formik.touched.address && formik.errors.address && (
                  <div className={classes.errorText}>{formik.errors.address}</div>
                )}
              </Box>
              <Box className={classes.aptContainer}>
                <Box className="commonLabel" component="label">&nbsp;Apt #</Box>
                <TextField
                  className="inputFormTopMargin"
                  id="outlined-basic"
                  placeholder="Apt #"
                  variant="outlined"
                  name="aptNo"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('aptNo')}
                  value={formik.values.aptNo || ''}
                />
                {formik.touched.aptNo && formik.errors.aptNo && (
                  <div className={classes.errorText}>{formik.errors.aptNo}</div>
                )}
              </Box>
            </Box>
            <Box className={`${classes.cityStateZipContainer} ${classes.subTextColor} `}>
              <Box className={classes.cityContainer}>
                <Box className={`${classes.cityLabel} commonLabel`} component="label">&nbsp;City</Box>
                <TextField
                  name="city"
                  className="inputFormTopMargin"
                  id="outlined-basic"
                  placeholder="City"
                  variant="outlined"
                  value={formik.values.city || ''}
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('city')}
                />
                {formik.touched.city && formik.errors.city && (
                  <div className={classes.errorText}>{formik.errors.city}</div>
                )}
              </Box>
              <Box className={classes.stateContainer}>
                <Box className="commonLabel" component="label">&nbsp;State</Box>
                <Select
                  className="inputFormTopMargin"
                  value={formik.values.state || ''}
                  onChange={formik.handleChange}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  placeholder="State"
                  name="state"
                  onBlur={() => formik.setFieldTouched('state')}
                >
                  <MenuItem value="">
                    None
                  </MenuItem>
                  {dropDownConstList.stateList.map((value) => <MenuItem value={value.name} key={value.abbreviation}>{value.name}</MenuItem>)}
                </Select>
                {formik.touched.state && formik.errors.state && (
                  <div className={classes.errorText}>{formik.errors.state}</div>
                )}
              </Box>
              <Box className={classes.zipContainer}>
                <Box className="commonLabel" component="label">&nbsp;Zip Code</Box>
                <TextField
                  className="inputFormTopMargin"
                  id="outlined-basic"
                  placeholder="Zip Code"
                  variant="outlined"
                  value={formik.values.postalCode || ''}
                  name="postalCode"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('postalCode')}
                />
                {formik.touched.postalCode && formik.errors.postalCode && (
                  <div className={classes.errorText}>{formik.errors.postalCode}</div>
                )}
              </Box>
            </Box>
            <Box className={`${classes.ssnBiosexContainer} ${classes.subTextColor} `}>
              <Box className={classes.ssnContainer}>
                <Box className={`${classes.tooltipLabel} commonLabel`} component="label">
                  &nbsp;Social Security #
                </Box>
                <TextField
                  className="inputFormTopMargin"
                  id="outlined-basic"
                  placeholder="Social Security #"
                  variant="outlined"
                  value={formik.values.ssn || ''}
                  name="ssn"
                  onChange={ssnOnChangeHandler}
                  onBlur={() => formik.setFieldTouched('ssn')}
                />
                {formik.touched.ssn && formik.errors.ssn && (
                  <div className={classes.errorText}>{formik.errors.ssn}</div>
                )}
              </Box>
              <Box className={classes.fullWidth}>
                <Box className={`${classes.tooltipLabel} commonLabel`} component="label">
                  &nbsp;Biological Sex
                  <Box
                    className={classes.tooltipLabel}
                    component="span"
                    onMouseOver={() => setBioSextooltip(true)}
                    onMouseOut={() => setBioSextooltip(false)}
                    onClick={() => {
                      if (windowSize.innerWidth > 768) return; setBioSextooltip((prevState) => !prevState);
                    }}
                  >
                    <Tooltip
                      open={bioSextooltip}
                      describeChild
                      title="We use this to determine an appropriate collector in the event that you require observed urine collections. Averhealth's policy is to match donors to collectors based on sex as assigned at birth."
                    >
                      <Help className={classes.toolTipIcon} />
                    </Tooltip>
                  </Box>
                </Box>
                <Select
                  className="inputFormTopMargin"
                  name="bioSex"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('bioSex')}
                  value={formik.values.bioSex || ''}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  placeholder="bioSex"
                >
                  <MenuItem value="">
                    None
                  </MenuItem>
                  {dropDownConstList.biologicalSexList.map((eachBioSexItem) => <MenuItem key={eachBioSexItem} value={eachBioSexItem}>{eachBioSexItem}</MenuItem>)}
                </Select>
                {formik.touched.bioSex && formik.errors.bioSex && (
                  <div className={classes.errorText}>{formik.errors.bioSex}</div>
                )}
              </Box>
            </Box>
            <Box className={`${classes.racePrimaryLangContainer} ${classes.subTextColor} `}>
              <Box className={classes.rangContainer}>
                <Box className={`${classes.raceLabel} commonLabel`} component="label">&nbsp;Race</Box>
                <Select
                  className="inputFormTopMargin"
                  name="race"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('race')}
                  value={formik.values.race || ''}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  placeholder="Race"
                >
                  <MenuItem value="">
                    None
                  </MenuItem>
                  {dropDownConstList.raceList.map((eachRaceItem) => <MenuItem key={eachRaceItem} value={eachRaceItem}>{eachRaceItem}</MenuItem>)}
                </Select>
                {formik.touched.race && formik.errors.race && (
                  <div className={classes.errorText}>{formik.errors.race}</div>
                )}
              </Box>
              <Box className={classes.fullWidth}>
                <Box className="commonLabel" component="label">&nbsp;Primary Language</Box>
                <Select
                  className="inputFormTopMargin"
                  name="primaryLang"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('primaryLang')}
                  value={formik.values.primaryLang || ''}
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  placeholder="Primary Language"
                >
                  <MenuItem value="">
                    None
                  </MenuItem>
                  {dropDownConstList.primaryLanguageList.map((eachPLItem) => <MenuItem key={eachPLItem} value={eachPLItem}>{eachPLItem}</MenuItem>)}
                </Select>
                {formik.touched.primaryLang && formik.errors.primaryLang && (
                  <div className={classes.errorText}>{formik.errors.primaryLang}</div>
                )}
              </Box>
            </Box>
            <Box className={`${classes.emailPhContainer} ${classes.subTextColor} `}>
              <Box className={classes.emailContainer}>
                <Box className={`${classes.emailLabel} commonLabel`} component="label">&nbsp;Email Address</Box>
                <TextField
                  name="email"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('email')}
                  value={formik.values.email || ''}
                  className="inputFormTopMargin"
                  id="email"
                  placeholder="Email Address"
                  variant="outlined"
                />
                {formik.touched.email && formik.errors.email && (
                  <div className={classes.errorText}>{formik.errors.email}</div>
                )}
              </Box>
              <Box className={classes.primaryLangContainer}>
                <Box className="commonLabel" component="label">&nbsp;Phone Number</Box>
                <TextField
                  name="phNo"
                  onChange={formik.handleChange}
                  onBlur={() => formik.setFieldTouched('phNo')}
                  value={formik.values.phNo || ''}
                  className="inputFormTopMargin"
                  id="outlined-basic"
                  placeholder="Phone Number"
                  variant="outlined"
                />
                {formik.touched.phNo && formik.errors.phNo && (
                  <div className={classes.errorText}>{formik.errors.phNo}</div>
                )}
              </Box>
            </Box>
            <Box className={classes.checkboxContainer}>
              <Checkbox
                name="smsNotifyAgree"
                onChange={formik.handleChange}
                checked={formik.values.smsNotifyAgree || false}
                {...label}
                icon={<CheckBoxOutlineBlank />}
                checkedIcon={<CheckBoxIcon />}
              />
              <Box className={classes.checkboxText} component="span">Enroll in SMS notifications for testing</Box>
            </Box>
            <Box className={classes.buttonsNavContainer}>
              <Button onClick={BackClickHandler} variant="contained" color="secondary">
                Back
              </Button>
              <FormPageNavBtns formPageNumber={4} activePgNum={1} onDotClick={onDotClick} />
              <Button
                className={nextBtnDisabledFunc(formik) ? classes.btnDisabled : ''}
                type="submit"
                variant="contained"
                color="primary"
              >
                Next
              </Button>
            </Box>
          </Box>
        </form>
      </FormCardWrap>
    </Container>
  );
};
